import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

type ParticipationGender = "male" | "female";

export type AgeGroupParticipationDatum = {
  ageGroup: string;
  maleCount: number;
  femaleCount: number;
};

type AgeGroupParticipationChartProps = {
  data: AgeGroupParticipationDatum[];
  title?: string;
  description?: string;
  titleClassName?: string;
  className?: string;
  withInnerChartBorder?: boolean;
};

function matchesParticipationGenderAgeGroup(
  ageGroup: string,
  gender: ParticipationGender,
): boolean {
  const normalized = ageGroup.trim().toUpperCase();
  if (!normalized) return false;
  if (normalized.startsWith("M")) return gender === "male";
  if (normalized.startsWith("F")) return gender === "female";
  return true;
}

export function AgeGroupParticipationChart({
  data,
  title = "Age Group Participation Breakdown",
  description,
  titleClassName = "text-lg font-semibold",
  className,
  withInnerChartBorder = false,
}: AgeGroupParticipationChartProps) {
  const [participationGender, setParticipationGender] =
    useState<ParticipationGender>("male");

  const rows = useMemo(
    () =>
      data
        .filter((row) =>
          matchesParticipationGenderAgeGroup(row.ageGroup, participationGender),
        )
        .map((row) => ({
          ageGroup: row.ageGroup,
          participants:
            participationGender === "male" ? row.maleCount : row.femaleCount,
        }))
        .filter((row) => row.participants > 0)
        .sort((a, b) => b.participants - a.participants),
    [data, participationGender],
  );

  const chartConfig = {
    participants: {
      label:
        participationGender === "male" ? "Male Finishers" : "Female Finishers",
      color:
        participationGender === "male" ? "var(--d8-cyan)" : "var(--d8-pink)",
    },
  } satisfies ChartConfig;

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className={titleClassName}>{title}</h2>
          {description ? (
            <p className="text-xs text-muted-foreground">{description}</p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Gender
          </span>
          <Select
            value={participationGender}
            onValueChange={(value) =>
              setParticipationGender(value as ParticipationGender)
            }
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Select gender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className={cn(withInnerChartBorder ? "rounded-lg border p-3" : undefined)}>
        {rows.length > 0 ? (
          <ChartContainer config={chartConfig} className="min-h-[320px] max-h-[520px]">
            <BarChart
              data={rows}
              layout="vertical"
              margin={{ top: 4, right: 12, left: 12, bottom: 4 }}
            >
              <CartesianGrid horizontal={false} />
              <XAxis type="number" allowDecimals={false} axisLine={false} tickLine={false} />
              <YAxis
                type="category"
                dataKey="ageGroup"
                width={80}
                axisLine={false}
                tickLine={false}
              />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    labelFormatter={(label) => `Age Group ${String(label)}`}
                    formatter={(value) => Number(value ?? 0).toLocaleString()}
                  />
                }
              />
              <Bar
                dataKey="participants"
                name={
                  participationGender === "male"
                    ? "Male Finishers"
                    : "Female Finishers"
                }
                fill="var(--color-participants)"
                radius={4}
              />
            </BarChart>
          </ChartContainer>
        ) : (
          <p className="text-sm text-muted-foreground">
            No age group participation data available.
          </p>
        )}
      </div>
    </div>
  );
}
